/**
 * ChurchScheduling Volunteer mobile app helpers.
 *
 * The dashboard offers a subtle "get the app" icon to signed-in, non-admin users
 * who are on a device whose store listing exists. Android (Google Play) is live.
 * iOS is wired up but inert: once Apple approves the app, set STORE_URLS.ios to
 * the App Store listing URL and iPhone/iPad users get the same icon, with no
 * other change. (Backend emails switch to the App Store link separately, via
 * MOBILE_APP_IOS_URL in the cs3.0 environment.)
 */

export const MOBILE_APP_NAME = "ChurchScheduling Volunteer";

const PLAY_LISTING_URL =
  "https://play.google.com/store/apps/details?id=com.churchscheduling.volunteer";

// Google Play install referrer: installs started from the dashboard show up under
// their own source in Play Console's acquisition reports.
const PLAY_REFERRER = encodeURIComponent(
  "utm_source=churchscheduling&utm_medium=web_dashboard&utm_campaign=volunteer_app&utm_content=navbar_icon"
);

export const STORE_URLS = {
  android: `${PLAY_LISTING_URL}&referrer=${PLAY_REFERRER}`,
  // Set to the App Store listing URL once Apple approves the app.
  ios: null,
};

export const STORE_NAMES = {
  android: "Google Play",
  ios: "the App Store",
};

/**
 * Which mobile platform the browser is running on: "android", "ios", or null
 * (desktop / unknown). Prefers the User-Agent Client Hints platform and falls
 * back to the User-Agent string.
 */
export function detectMobilePlatform(
  nav = typeof navigator !== "undefined" ? navigator : null
) {
  if (!nav) return null;

  const hintedPlatform = String(nav.userAgentData?.platform || "").toLowerCase();
  const userAgent = String(nav.userAgent || "");

  if (hintedPlatform === "android" || /android/i.test(userAgent)) return "android";

  // iPadOS 13+ identifies itself as a Mac, so also look for a touch screen.
  const isIpadOs = nav.platform === "MacIntel" && Number(nav.maxTouchPoints) > 1;
  if (/iphone|ipad|ipod/i.test(userAgent) || isIpadOs) return "ios";

  return null;
}

/**
 * Store listing URL for a platform, or null when that platform has no live listing yet.
 */
export function getMobileAppStoreUrl(platform) {
  return (platform && STORE_URLS[platform]) || null;
}

/**
 * True when the signed-in user has any admin role. The scoped admin levels
 * (activity, workgroup, instance, ...) count too: only people with no admin
 * role at all are offered the volunteer app icon.
 */
export function isAdminSession(sessionData, permissionData) {
  const session = sessionData || {};
  const permissions = permissionData || {};

  return Boolean(
    permissions.admin_level ||
      session.isAdminInLocation ||
      session.isSuperAdmin ||
      session.is_super_admin ||
      session.organizationadmin ||
      session.locationadmin ||
      session.hasLocationAdminPermission
  );
}

/**
 * Whether to show the install icon: signed-in, non-admin, on a platform that
 * has a live store listing.
 */
export function shouldOfferMobileApp({ isSignedIn, isAdmin, platform } = {}) {
  if (!isSignedIn || isAdmin) return false;

  return Boolean(getMobileAppStoreUrl(platform));
}
